import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    HStack,
    Spinner,
    Text,
    useDisclosure,
    VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { translate } from '../../../i18n';
import { GameAdminCommentCategory } from '../../../models/game/dto/game-admin-comment.dto';
import { GameDto } from '../../../models/game/dto/game.dto';
import { useGetGameAdminComments } from '../hooks/useGameAdminComments';
import AdminCommentCard from './AdminCommentCard';
import { categoryColors, categoryLabels } from './adminCommentConstants';
import CreateAdminCommentModal from './CreateAdminCommentModal';

interface AdminCommentsSectionProps {
    game: GameDto;
}

export default function AdminCommentsSection({ game }: AdminCommentsSectionProps) {
    const { data: comments, isLoading, error } = useGetGameAdminComments(game.id);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedCategory, setSelectedCategory] = useState<GameAdminCommentCategory | 'all'>('all');

    // Filter comments by category
    const filteredComments = comments?.filter(comment =>
        selectedCategory === 'all' || comment.category === selectedCategory
    ) || [];

    const categoryCounts = useMemo(() => {
        if (!comments) return { all: 0 } as Record<string, number>;
        const counts: Record<string, number> = { all: comments.length };
        for (const cat of Object.values(GameAdminCommentCategory)) {
            counts[cat] = comments.filter(c => c.category === cat).length;
        }
        return counts;
    }, [comments]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Spinner size="lg" />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert status="error">
                <AlertIcon />
                {translate('admin_comments.load_error')}
            </Alert>
        );
    }

    return (
        <VStack spacing={4} align="stretch" px={3}>
            {/* Add Comment button */}
            <HStack justify="flex-end">
                <Button colorScheme="brand" size="sm" onClick={onOpen}>
                    {translate('admin_comments.add_comment')}
                </Button>
            </HStack>

            {/* Category filter tabs */}
            <HStack spacing={2} flexWrap="wrap">
                <Badge
                    colorScheme={selectedCategory === 'all' ? 'brand' : 'gray'}
                    cursor="pointer"
                    px={3}
                    py={1}
                    onClick={() => setSelectedCategory('all')}
                >
                    {translate('admin_comments.all')} ({categoryCounts.all})
                </Badge>

                {Object.values(GameAdminCommentCategory).map(category => (
                    <Badge
                        key={category}
                        colorScheme={selectedCategory === category ? categoryColors[category] : 'gray'}
                        cursor="pointer"
                        px={3}
                        py={1}
                        onClick={() => setSelectedCategory(category)}
                    >
                        {categoryLabels[category]} ({categoryCounts[category]})
                    </Badge>
                ))}
            </HStack>

            {/* Comments list */}
            <VStack spacing={3} align="stretch" pb={3}>
                {filteredComments.length === 0 ? (
                    <Box textAlign="center" py={8}>
                        <Text color="gray.500">
                            {selectedCategory === 'all'
                                ? translate('admin_comments.no_comments')
                                : translate('admin_comments.no_comments_category')
                            }
                        </Text>
                    </Box>
                ) : (
                    filteredComments.map(comment => (
                        <AdminCommentCard
                            key={comment.id}
                            comment={comment}
                            gameId={game.id}
                        />
                    ))
                )}
            </VStack>

            {/* Create Comment Modal */}
            <CreateAdminCommentModal
                isOpen={isOpen}
                onClose={onClose}
                gameId={game.id}
            />
        </VStack>
    );
}
